"use client";

import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Asks a signed-in user for their school/institution when we do not have one.
 *
 * Requirement 3: the information is optional. Skipping is a first-class action —
 * nothing is written, and the prompt returns on the next login. Once the user
 * provides an institution it is never asked for again.
 */
export function UserProfilePrompt() {
  const { needsProfilePrompt, profile, saveProfile, dismissProfilePrompt } = useAuth();
  const { translate } = useLanguage();
  const [institution, setInstitution] = useState("");
  const [country, setCountry] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Prefill from whatever the record already has (e.g. a country from sign-up).
  useEffect(() => {
    if (!needsProfilePrompt) return;
    setInstitution(profile?.institution ?? "");
    setCountry(profile?.country ?? "");
    setError("");
  }, [needsProfilePrompt, profile?.institution, profile?.country]);

  if (!needsProfilePrompt) return null;

  const handleSave = async () => {
    if (!institution.trim() && !country.trim()) {
      // Nothing filled in — treat "Save" as "Skip" rather than writing blanks.
      dismissProfilePrompt();
      return;
    }
    setSaving(true);
    setError("");
    try {
      await saveProfile({ institution, country });
      if (!institution.trim()) {
        // Country only: the institution question stands, but not this login.
        dismissProfilePrompt();
      }
    } catch {
      setError(translate("profileSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && dismissProfilePrompt()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{translate("profilePromptTitle")}</DialogTitle>
          <DialogDescription>
            {translate("profilePromptDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <label
              htmlFor="profile-institution"
              className="block text-xs font-semibold text-foreground"
            >
              {translate("schoolInstitution")} ({translate("optionalField")})
            </label>
            <input
              id="profile-institution"
              type="text"
              value={institution}
              onChange={(e) => setInstitution(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !saving && handleSave()}
              placeholder={translate("schoolInstitutionPlaceholder")}
              className="w-full px-3 py-2 text-sm rounded-md border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={saving}
              autoFocus
            />
          </div>

          <div className="space-y-1">
            <label
              htmlFor="profile-country"
              className="block text-xs font-semibold text-foreground"
            >
              {translate("countryRegion")} ({translate("optionalField")})
            </label>
            <input
              id="profile-country"
              type="text"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !saving && handleSave()}
              placeholder={translate("countryRegionPlaceholder")}
              className="w-full px-3 py-2 text-sm rounded-md border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={saving}
            />
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={dismissProfilePrompt}
            disabled={saving}
            className="cursor-pointer"
          >
            {translate("skipForNow")}
          </Button>
          <Button onClick={handleSave} disabled={saving} className="cursor-pointer">
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {translate("saving")}
              </>
            ) : (
              translate("save")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
